import { type ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { BrandMark, focusRing } from "../../ui/index.js";
import { LocaleSwitch } from "../auth/index.js";

/**
 * Язык публичной страницы: выбор посетителя (переключатель, хранится на устройстве), иначе язык салона.
 * Язык салона не записываем на устройство: это не выбор посетителя.
 */
export function usePublicLocale(salonLocale: string | undefined): void {
  const { i18n } = useTranslation();
  useEffect(() => {
    if (!salonLocale) return;
    let stored: string | null = null;
    try {
      stored = localStorage.getItem("locale");
    } catch {
      stored = null;
    }
    if (stored) return;
    const ui = salonLocale.startsWith("sr") ? "sr-Latn" : salonLocale === "ru" || salonLocale === "en" ? salonLocale : "sr-Latn";
    if (i18n.language !== ui) void i18n.changeLanguage(ui);
  }, [salonLocale, i18n]);
}

/**
 * Оболочка публичных страниц салона (запись, «моя запись», уведомление о данных): под телефон, без входа.
 * Название салона в шапке, язык справа; от `sm` содержимое в белой карточке по центру на сером фоне.
 */
export function PublicShell({ salonName, slug, children }: { salonName?: string; slug?: string; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-dvh w-full justify-center px-4 py-4 sm:py-8">
      <div className="flex w-full max-w-md flex-col">
        <header className="mb-4 flex items-center justify-between gap-3">
          <div className="min-w-0 truncate text-lg font-semibold text-ink">{salonName ?? ""}</div>
          <LocaleSwitch />
        </header>
        {/* Карточка по высоте содержимого; подвал прижат к низу экрана распоркой. */}
        <main className="sm:rounded-sheet sm:border sm:border-line sm:bg-white sm:p-6 sm:shadow-card">{children}</main>
        <div className="flex-1" aria-hidden="true" />
        <footer className="mt-8 flex flex-col gap-2 border-t border-line pt-4 text-xs text-ink-3">
          {slug && (
            <Link
              to={`/data-notice/${slug}`}
              className={`inline-flex min-h-8 items-center self-start rounded underline underline-offset-2 hover:text-ink ${focusRing}`}
            >
              {t("book.dataNotice")}
            </Link>
          )}
          <p className="flex items-center gap-1.5">
            <BrandMark className="h-4 w-4" />
            {t("book.poweredBy")}
          </p>
        </footer>
      </div>
    </div>
  );
}
